import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { TQuotaAgent } from '../types/agent.type';

export type TGetQuotaAgentResponse = ApiResponse<TQuotaAgent>;

export const getQuotaAgent = async (): Promise<TGetQuotaAgentResponse> => {
  const url = '/user/manage/get-quota';

  return axiosInstance.get(url);
};

export const GET_QUOTA_AGENT_QUERY_KEY = 'get-quota-agent';

export const getQuotaAgentQueryOptions = <TData = TQuotaAgent>(select?: (data: TGetQuotaAgentResponse) => TData) => {
  return queryOptions({
    queryKey: [GET_QUOTA_AGENT_QUERY_KEY],
    queryFn: () => getQuotaAgent(),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetQuotaAgentQuery<TData>(options: {
  queryConfig?: QueryConfig<typeof getQuotaAgentQueryOptions<TData>>;
  select: (data: TGetQuotaAgentResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetQuotaAgentQuery(options: {
  queryConfig?: QueryConfig<typeof getQuotaAgentQueryOptions<TGetQuotaAgentResponse>>;
}): ReturnType<typeof useQuery<TGetQuotaAgentResponse>>;

// Implementation
export function useGetQuotaAgentQuery<TData = TGetQuotaAgentResponse>({
  queryConfig,
  select,
}: {
  queryConfig?: QueryConfig<typeof getQuotaAgentQueryOptions<TData | TGetQuotaAgentResponse>>;
  select?: (data: TGetQuotaAgentResponse) => TData;
}) {
  return useQuery({
    ...getQuotaAgentQueryOptions(select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetQuotaAgentResponse ? TGetQuotaAgentResponse : TData>>;
}
